import { Reducer } from "redux";
import { produce } from "immer";

import { FetchingState, ParametricReducer } from "@multiweb/types/react";
import { DATA_SOURCE_INIT_STATE } from "../initState";
import {
    IDataSourceLoading,
    ILoadDataAction,
    ILoadDataErrorAction,
    ILoadDataSuccessAction,
    IRefreshDataAction,
    IRefreshDataErrorAction,
    IRefreshDataSuccessAction,
    ISetDisableLoadingAction,
    IUpdateMasterFrameDataSourceCondition,
} from "../types";
import { hasNextItems } from "../utils";
import { RecordIdType } from "./types";

const fetchingState: Reducer<FetchingState> = (
    state = DATA_SOURCE_INIT_STATE.loading.fetchingState,
    action,
) => {
    if (action instanceof ILoadDataAction) {
        // Nic neprovede, pokud DataSource již načítá data
        if (state === FetchingState.request) {
            return state;
        }
        return FetchingState.request;
    } else if (action instanceof IRefreshDataAction) {
        return FetchingState.request;
    } else if (action instanceof ILoadDataSuccessAction || action instanceof IRefreshDataSuccessAction) {
        return FetchingState.success;
    } else if (action instanceof ILoadDataErrorAction || action instanceof IRefreshDataErrorAction) {
        return FetchingState.error;
    }
    return state;
};

const startIndex: Reducer<number> = (
    state = DATA_SOURCE_INIT_STATE.loading.startIndex,
    action,
) => {
    if (action instanceof ILoadDataSuccessAction) {
        return state + action.payload.data.length;
    } else if (action instanceof IRefreshDataSuccessAction) {
        return action.payload.data.length;
    }
    return state;
};

type FetchingCountReducerParams = {
    readonly fetchingState: FetchingState; // Při načítání dat potřebuje vědět na základě FetchingState, zdali se již nenačítají data.
    readonly recordsIds: ReadonlyArray<RecordIdType>; // Při načítání dat potřebuje vědět počet záznamů.
};

const fetchingCount: ParametricReducer<number, FetchingCountReducerParams> = (
    state = DATA_SOURCE_INIT_STATE.loading.fetchingCount,
    action,
    reducerParams,
) => {
    if (action instanceof ILoadDataAction) {
        const { fetchingState } = reducerParams;
        // Nic neprovede, pokud DataSource již načítá data
        if (fetchingState === FetchingState.request) {
            return state;
        }
        return action.payload.fetchCount;
    } else if (action instanceof IRefreshDataAction) {
        const { recordsIds } = reducerParams;
        return recordsIds.length;
    } else if (
        action instanceof ILoadDataSuccessAction ||
        action instanceof IRefreshDataSuccessAction ||
        action instanceof ILoadDataErrorAction ||
        action instanceof IRefreshDataErrorAction
    ) {
        return 0;
    }
    return state;
};

const hasNextItemsReducer: ParametricReducer<boolean, number> = (
    state = DATA_SOURCE_INIT_STATE.loading.hasNextItems,
    action,
    fetchingCount,
) => {
    if (action instanceof IRefreshDataAction) {
        return true;
    } else if (action instanceof ILoadDataSuccessAction || action instanceof IRefreshDataSuccessAction) {
        return hasNextItems(fetchingCount, action.payload.data.length);
    } else if (action instanceof ILoadDataErrorAction || action instanceof IRefreshDataErrorAction) {
        return false;
    }
    return state;
};

const disabledLoading: Reducer<boolean> = (
    state = DATA_SOURCE_INIT_STATE.loading.disabledLoading,
    action,
) => {
    if (action instanceof IUpdateMasterFrameDataSourceCondition) {
        const { isMasterRecordSelected = true } = action.payload;
        return !isMasterRecordSelected;
    } else if (action instanceof ISetDisableLoadingAction) {
        return action.payload.value;
    }
    return state;
};

export const loadingReducer: ParametricReducer<IDataSourceLoading, ReadonlyArray<RecordIdType>> = (
    state = DATA_SOURCE_INIT_STATE.loading,
    action,
    recordsIds,
) => {
    return produce(state, (draft) => {
        const fetchingCountState = fetchingCount(draft.fetchingCount, action, {
            recordsIds,
            fetchingState: draft.fetchingState,
        });
        const fetchingStateState = fetchingState(draft.fetchingState, action);
        const startIndexState = startIndex(draft.startIndex, action);
        const hasNextItemsState = hasNextItemsReducer(draft.hasNextItems, action, draft.fetchingCount);
        const disabledLoadingState = disabledLoading(draft.disabledLoading, action);

        draft.fetchingState = fetchingStateState;
        draft.startIndex = startIndexState;
        draft.fetchingCount = fetchingCountState;
        draft.hasNextItems = hasNextItemsState;
        draft.disabledLoading = disabledLoadingState;
    });
};
